/**
 * The first-run panel: one request, and a component that is allowed to render
 * nothing.
 *
 * It is mounted at the top of `/` today and is written so that it can be moved
 * without being rewritten — it takes no props but a `className`, owns its own
 * read, and knows nothing about the screen around it. Slice 20 rebuilds the
 * Overview as a dashboard and will decide where this sits then; slices 16 and 17
 * may want it on a screen that never fetches `GET /overview`. That is the whole
 * reason the checklist is not fed from the dashboard response.
 *
 * ## It asks whether this workspace has Connect before it asks anything else
 *
 * Every step here is Connect work (slice 65 renamed the pillar; before it, this
 * said Reach), and `GET /onboarding` deliberately does not
 * check entitlement — its service header hands that decision to the web app in
 * so many words. {@link onboardingPanelApplies} is where the web app takes it,
 * on the sidebar's own `navPillar` verdict so the two cannot disagree, and this
 * component asks it **first**: a workspace whose sidebar says Connect is not on
 * this plan must not also be handed five buttons into it.
 *
 * That gate is why there are two components below rather than one. Hooks cannot
 * be called conditionally, so a single component would have to fire the request
 * and then throw the answer away — a request per Overview load on behalf of a
 * tenant who will never see the panel. The outer function reads the session and
 * decides; the inner one is mounted only when the answer is yes, and owns the
 * read. The gate travels **with** the component rather than sitting at its mount
 * site, which is what keeps decision 5 true: moving the panel to Integrations
 * moves the entitlement check with it, and there is no line on another screen
 * for slice 20 to drop.
 *
 * ## It disappears when it is done, and there is nothing behind it
 *
 * `setup: 'complete'` renders `null`. **Nothing replaces the panel** — the
 * Overview goes back to being the Overview — and that is the answer rather than
 * a gap: every fact the checklist derived from is still visible on the screen
 * that owns it, the connection on Integrations, the contacts on Audiences, the
 * campaign in the recent-campaigns list. Nothing is lost by it leaving, which is
 * the test of whether it should stay. A congratulations card in its place would
 * be a permanent tile celebrating something that happened once, and a checklist
 * that stays for ever after every step is complete is exactly the clutter that
 * teaches people to stop reading the first screen.
 *
 * It comes back on its own if a step stops being true, because nothing was ever
 * stored. That is a feature of the contract, not of this file.
 *
 * ## The three async states, and why the first one draws nothing
 *
 * `useAsyncData`, the same pattern every feature screen uses. What is unusual is
 * the loading state: **while the first read is in flight this renders nothing,
 * not a skeleton.** For most workspaces most of the time the answer is
 * `complete`, so a placeholder here would be a card that appears and then
 * vanishes on every visit to the Overview — a promise the response usually does
 * not keep, and a layout shift bought for nothing. The screen it sits on is
 * drawing its own loader below at that moment, so nobody is looking at a blank
 * page waiting for this.
 *
 * A **failure is not silence**, and that distinction is the contract's own rule
 * carried into the component: "we could not find out" and "there is nothing
 * there" are different statements, and a failed read must never be rendered as a
 * checklist of things you have not done. So the error arm draws a small
 * `ErrorState` with a retry — `ErrorState` rather than `FormAlert`, because
 * nothing was submitted and this is a read that did not arrive. It is compact on
 * purpose: a full-height error block at the top of the Overview would push the
 * two rates that matter below the fold to report that a nicety is down. Once it
 * has drawn one it keeps the frame through the retry instead of blinking out —
 * see `failedOnce`, which exists precisely because "renders nothing while
 * loading" is the wrong answer for the second load rather than the first.
 *
 * The refresh-over-stale-data arm keeps the last checklist on screen, which is
 * `useAsyncData`'s own contract. It is close to unreachable here — the only
 * `reload()` is on the error state, which by definition has no data — and it is
 * left as the hook's behaviour rather than special-cased into something this
 * file would then have to keep true.
 */
import { Card } from '@wend/ui';
import { useEffect, useState } from 'react';

import { useAuth } from '../../auth/auth-context';
import { ErrorState } from '../../components/ErrorState';
import { fetchOnboarding } from '../../lib/api/onboarding';
import { useAsyncData } from '../../lib/use-async-data';

import { OnboardingChecklist } from './OnboardingChecklist';
import { onboardingPanelApplies } from './onboarding-entitlement';

export interface OnboardingPanelProps {
  /**
   * Spacing belongs to the layout that mounts this, not to the panel — and when
   * the panel renders nothing, its margin correctly goes with it.
   */
  className?: string;
}

export function OnboardingPanel({ className }: OnboardingPanelProps) {
  const { user } = useAuth();

  // Before the request, not after it: a workspace without Connect is not asked
  // about, not rendered for, and not told to go and start sending on the same
  // screen its sidebar says Connect is not on this plan.
  if (!onboardingPanelApplies(user?.features)) return null;

  return <OnboardingPanelForConnect className={className} />;
}

/**
 * The panel proper: one read, three states, and `null` when there is nothing
 * left to do. Reached only through the gate above, which is why nothing in here
 * mentions entitlement.
 */
function OnboardingPanelForConnect({ className }: OnboardingPanelProps) {
  const { data, error, status, reload } = useAsyncData((signal) => fetchOnboarding(signal), []);

  /*
   * Remembered because the loading state here is *nothing*, and "nothing" is the
   * wrong answer for a retry: `useAsyncData` clears the error the moment a
   * reload starts, so without this the Try again button would make the whole
   * block vanish under the reader's cursor and reappear only if it failed a
   * second time. Once this panel has reported a failure it keeps the frame and
   * marks the button busy — which is what `ErrorState.retrying` is for.
   */
  const [failedOnce, setFailedOnce] = useState(false);
  useEffect(() => {
    if (status === 'error') setFailedOnce(true);
  }, [status]);

  if (data === null) {
    // Still loading, and nothing has gone wrong yet: draw nothing at all.
    if (status !== 'error' && !failedOnce) return null;

    return (
      <Card className={className}>
        <ErrorState
          error={error}
          title="Setup checklist did not load"
          onRetry={reload}
          retrying={status === 'loading'}
          className="flex flex-col items-start gap-3 p-[18px]"
        />
      </Card>
    );
  }

  if (data.setup === 'complete') return null;

  return <OnboardingChecklist steps={data.steps} next={data.next} className={className} />;
}
